import { useEffect, useRef } from 'react'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { DEV_OVERRIDE_AUTH_ACCOUNT, DEV_OVERRIDE_LOYALTY_STATE, SYNC_USER_DETAILS } from 'actions/actionConstants'
import { addMonths } from 'lib/datetime/dateUtils'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'

const SESSION_KEY = 'DevPanel:customerProfileOverrides'

function DevCustomerProfileRestore() {
  const dispatch = useAppDispatch()
  const initComplete = useAppSelector(state => state.auth.initComplete)
  const loyaltyFetched = useAppSelector(state => !state.loyalty.account.fetching && !state.loyalty.account.initial)
  const configLoaded = useAppSelector(state => !!state.loyalty.config)
  const currentRegion = useAppSelector(state => state.geo.currentRegionCode)
  const currentCurrency = useAppSelector(state => state.geo.currentCurrency)
  const restored = useRef(false)

  useEffect(() => {
    if (!initComplete || !loyaltyFetched || !configLoaded || restored.current) return
    restored.current = true

    const overrides: Record<string, any> | null = isomorphicSessionStorage.get(SESSION_KEY)
    if (!overrides || Object.keys(overrides).length === 0) return

    const loyaltyOverrides: Record<string, any> = { region: currentRegion }
    if (overrides.tier !== undefined) loyaltyOverrides.tier = overrides.tier
    if (overrides.points !== undefined) loyaltyOverrides.points = overrides.points
    if (overrides.statusCredits !== undefined) loyaltyOverrides.statusCredits = overrides.statusCredits
    if (overrides.numberOfNights !== undefined) loyaltyOverrides.numberOfNights = overrides.numberOfNights
    if (overrides.societeJoinDate) {
      const reviewDate = addMonths(new Date(overrides.societeJoinDate + 'T00:00:00'), 12)
      loyaltyOverrides.statusReviewDate = reviewDate.toISOString().split('T')[0]
    }
    if (overrides.hasAccount !== undefined) loyaltyOverrides.hasAccount = overrides.hasAccount

    if (Object.keys(loyaltyOverrides).length > 1) {
      dispatch({ type: DEV_OVERRIDE_LOYALTY_STATE, overrides: loyaltyOverrides })
    }

    if (overrides.luxPlusActive !== undefined) {
      dispatch({
        type: DEV_OVERRIDE_AUTH_ACCOUNT,
        overrides: {
          luxPlusActive: overrides.luxPlusActive,
          luxPlusRegion: currentRegion,
          ...(overrides.luxPlusActive && overrides.luxPlusMembershipState
            ? { luxPlusMembershipState: overrides.luxPlusMembershipState }
            : {}),
        },
      })
    } else if (overrides.luxPlusMembershipState) {
      dispatch({
        type: DEV_OVERRIDE_AUTH_ACCOUNT,
        overrides: { luxPlusMembershipState: overrides.luxPlusMembershipState },
      })
    }

    if (overrides.luxPlusJoinDate) {
      dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { luxPlusJoinDate: overrides.luxPlusJoinDate } })
    }

    if (overrides.luxPlusSavings !== undefined && overrides.luxPlusSavings > 0) {
      dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { luxPlusSavings: overrides.luxPlusSavings } })
    }

    if (overrides.credits !== undefined) {
      dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { balance: overrides.credits, currencyCode: currentCurrency } })
    }

    if (overrides.givenName !== undefined || overrides.surname !== undefined) {
      const givenName = overrides.givenName ?? ''
      const surname = overrides.surname ?? ''
      dispatch({
        type: SYNC_USER_DETAILS,
        userDetails: {
          givenName,
          surname,
          fullName: `${givenName} ${surname}`.trim(),
        },
      })
    }
  }, [dispatch, initComplete, loyaltyFetched, configLoaded, currentCurrency, currentRegion])

  return null
}

export default DevCustomerProfileRestore
